import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import GridContainer from '@jumbo/components/GridContainer';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import CmtCard from '@coremat/CmtCard';
import { Box, Button, Fab, Grid, List, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import useTableDataParams from 'services/Hooks/useTableDataParams';
import { useCarts } from 'services/Data/CartService';
import Currency from 'react-currency-formatter';
import { useDealer } from 'services/Data/DealerServices';
import { Money, Visibility } from '@mui/icons-material';
import CmtGridView from '@coremat/CmtGridView';
import useWidth from 'services/Hooks/useWidth';
import useTranslateEnum from 'services/Hooks/useTranslateEnum';
import CmtCardFooter from '@coremat/CmtCard/CmtCardFooter';
import CartStatusEnum, { CartStatusEnumValue } from 'enums/CartStatusEnum';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';
import { useExteriorSignageQuotes } from 'services/Data/ExteriorSignageQuoteService';
import ExteriorSignageQuoteStatusEnum, { ExteriorSignageQuoteStatusEnumValue } from 'enums/ExteriorSignageQuoteStatusEnum';
import { useMerchandisingProgramQuotes } from 'services/Data/MerchandisingProgramQuoteService';
import MerchandisingProgramQuoteStatusEnum, {
  MerchandisingProgramQuoteStatusEnumValue,
} from 'enums/MerchandisingProgramQuoteStatusEnum';
import { ApplicationIcons } from 'icons/ApplicationIcons';
import GridItem from '@jumbo/components/GridItem';
import ProfileRequestListItem from './ProfileRequestListItem';
import { useApprovalNotification } from 'services/Data/NotificationService';
import { useSelector } from 'react-redux';
import { IsDSM, IsEmployee } from 'config/config';

const ProfileAwaitingRequests = () => {
  const { t } = useTranslation();
  const { translateEnum } = useTranslateEnum();
  const accountReducer = useSelector(state => state.AccountReducer);
  const showDealerColumn = IsDSM || IsEmployee;

  const theme = useTheme();
  const width = useWidth();

  const { data: dealer } = useDealer();
  const { data: notification } = useApprovalNotification(accountReducer.isLoggedIn, true);

  const [tableDataParams] = useTableDataParams({
    sorts: [{ column: 'dateCreated', asc: false }],
    filters: [
      {
        selectedColumn: {
          column: {
            filterColumn: 'Status',
            type: 'bool',
          },
        },
        comparator: `{0} >= ${CartStatusEnumValue.PendingBrpApproval}`,
        value: CartStatusEnumValue.PendingBrpApproval,
      },
      {
        selectedColumn: {
          column: {
            filterColumn: 'Status',
            type: 'bool',
          },
        },
        comparator: `{0} < ${CartStatusEnumValue.Shipped}`,
        value: CartStatusEnumValue.PendingBrpApproval,
      },
    ],
  });

  const { data: carts } = useCarts(tableDataParams);

  const [exteriorSignageQuoteTableDataParams] = useTableDataParams({
    sorts: [{ column: 'dateCreated', asc: false }],
    filters: [
      {
        selectedColumn: {
          column: {
            filterColumn: 'Status',
            type: 'bool',
          },
        },
        comparator: `{0} >= ${ExteriorSignageQuoteStatusEnumValue.Submitted}`,
        value: ExteriorSignageQuoteStatusEnumValue.Submitted,
      },
      {
        selectedColumn: {
          column: {
            filterColumn: 'Status',
            type: 'bool',
          },
        },
        comparator: `{0} <= ${ExteriorSignageQuoteStatusEnumValue.PendingPictures}`,
        value: ExteriorSignageQuoteStatusEnumValue.PendingPictures,
      },
    ],
  });

  const { data: exteriorSignageQuotes } = useExteriorSignageQuotes(exteriorSignageQuoteTableDataParams);

  const [merchandisingProgramQuoteTableDataParams] = useTableDataParams({
    sorts: [{ column: 'dateCreated', asc: false }],
    filters: [
      {
        selectedColumn: {
          column: {
            filterColumn: 'Status',
            type: 'bool',
          },
        },
        comparator: `{0} >= ${MerchandisingProgramQuoteStatusEnumValue.InProcess}`,
        value: MerchandisingProgramQuoteStatusEnumValue.InProcess,
      },
      {
        selectedColumn: {
          column: {
            filterColumn: 'Status',
            type: 'bool',
          },
        },
        comparator: `{0} <= ${MerchandisingProgramQuoteStatusEnumValue.Enroll}`,
        value: MerchandisingProgramQuoteStatusEnumValue.Enroll,
      },
    ],
  });

  const { data: merchandisingProgramQuotes } = useMerchandisingProgramQuotes(merchandisingProgramQuoteTableDataParams);

  const [currentTab, setCurrentTab] = useState(0);
  return (
    <Box>
      <CmtCard>
        <CmtCardContent>
          <GridContainer>
            <GridItem xs={12} md={2}>
              <List>
                <ProfileRequestListItem
                  currentTab={currentTab}
                  setCurrentTab={newTabValue => setCurrentTab(newTabValue)}
                  icon={<ApplicationIcons.Cart />}
                  primaryText={t('pendingOrders')}
                  tabValue={0}
                  Notifications={notification?.cartsNeedApproval}
                />

                <ProfileRequestListItem
                  currentTab={currentTab}
                  setCurrentTab={newTabValue => setCurrentTab(newTabValue)}
                  icon={<ApplicationIcons.ExteriorSignage />}
                  primaryText={t('submittedExteriorSignageQuotes')}
                  tabValue={1}
                  Notifications={notification?.exteriorSignageNeedApproval}
                />

                <ProfileRequestListItem
                  currentTab={currentTab}
                  setCurrentTab={newTabValue => setCurrentTab(newTabValue)}
                  icon={<ApplicationIcons.MerchandisingProgram />}
                  primaryText={t('submittedMerchandisingProgramQuotes')}
                  tabValue={2}
                />
              </List>
            </GridItem>

            <GridItem xs={12} md={10}>
              {currentTab === 0 && (
                <CmtCard>
                  <CmtCardHeader
                    style={{ minHeight: '60px' }}
                    separator={{
                      color: theme.palette.borderColor.dark,
                    }}
                    title={t('pendingOrders')}
                  />
                  <CmtCardContent>
                    <Typography variant="h4">{t('awaitingOrderInfo')}</Typography>
                    {width === 'xs' ? (
                      carts?.items?.map(cart => (
                        <CmtCard key={cart.id}>
                          <CmtCardHeader
                            separator={{
                              color: theme.palette.borderColor.dark,
                            }}
                            title={
                              cart.cartStatusHistories?.find(
                                history => history.newStatus === CartStatusEnumValue.PendingBrpApproval,
                              )?.dateStatusChanged
                                ? moment(
                                    cart.cartStatusHistories?.find(
                                      history => history.newStatus === CartStatusEnum.PendingBrpApproval,
                                    )?.dateStatusChanged,
                                  ).format(t('date_format'))
                                : ''
                            }
                            subTitle={translateEnum(CartStatusEnum, cart.status)}
                          />
                          <CmtCardContent>
                            <CmtGridView
                              itemPadding={24}
                              responsive={{
                                xs: 1,
                                sm: 2,
                                md: 3,
                                lg: 3,
                                xl: 3,
                              }}
                              data={[
                                {
                                  title: t('total'),
                                  desc: <Currency quantity={cart.total} currency={dealer?.currenceName} />,
                                  icon: <Money />,
                                },
                              ]}
                              renderRow={(item, index) => (
                                <Box display="flex" alignItems="center" key={index}>
                                  {item.icon}
                                  <Box ml={3}>
                                    <Box fontSize={12} color="text.secondary">
                                      {item.title}
                                    </Box>
                                    <Box>{item.desc ? item.desc : item.list}</Box>
                                  </Box>
                                </Box>
                              )}
                            />
                          </CmtCardContent>
                          <CmtCardFooter>
                            <Grid item xs={12} textAlign="center">
                              <Link to={`/dealer/profile/awaiting-requests/order/${cart.id}`}>
                                <Button variant="outlined">{t('see_order')}</Button>
                              </Link>
                            </Grid>
                          </CmtCardFooter>
                        </CmtCard>
                      ))
                    ) : (
                      <Table>
                        <TableHead>
                          <TableRow>
                            {showDealerColumn && <TableCell>{t('dealer')}</TableCell>}
                            <TableCell>{t('orderNumber')}</TableCell>
                            <TableCell>{t('date')}</TableCell>
                            <TableCell>{t('status')}</TableCell>
                            <TableCell>{t('total')}</TableCell>
                            <TableCell>{t('actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {carts?.items?.map(cart => {
                            return (
                              <TableRow
                                key={cart.id}
                                style={
                                  cart.status === CartStatusEnumValue.PendingDealerApproval
                                    ? {
                                        backgroundColor: '#f6d5d5',
                                      }
                                    : {}
                                }>
                                {showDealerColumn && <TableCell>{cart.dealerName}</TableCell>}
                                <TableCell>{cart.orderNumber}</TableCell>
                                <TableCell>
                                  {cart.cartStatusHistories?.find(
                                    history => history.newStatus === CartStatusEnumValue.PendingBrpApproval,
                                  )?.dateStatusChanged
                                    ? moment(
                                        cart.cartStatusHistories?.find(
                                          history => history.newStatus === CartStatusEnum.PendingBrpApproval,
                                        )?.dateStatusChanged,
                                      ).format(t('date_format'))
                                    : ''}
                                </TableCell>
                                <TableCell>{translateEnum(CartStatusEnum, cart.status)}</TableCell>
                                <TableCell>
                                  <Currency quantity={cart.total} currency={dealer?.currenceName} />
                                </TableCell>
                                <TableCell>
                                  <ThemedTooltip title={t('see_cart')} placement={'top'}>
                                    <Link to={`/dealer/profile/awaiting-requests/order/${cart.id}`}>
                                      <Fab
                                        color="primary"
                                        sx={{
                                          height: 26,
                                          width: 26,
                                          minHeight: 26,
                                          '& svg': {
                                            height: 14,
                                            width: 14,
                                          },
                                        }}>
                                        <Visibility />
                                      </Fab>
                                    </Link>
                                  </ThemedTooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </CmtCardContent>
                </CmtCard>
              )}

              {currentTab === 1 && (
                <CmtCard>
                  <CmtCardHeader
                    style={{ minHeight: '60px' }}
                    separator={{
                      color: theme.palette.borderColor.dark,
                    }}
                    title={t('submittedExteriorSignageQuotes')}
                  />
                  <CmtCardContent>
                    <Typography variant="h4">{t('submittedExteriorSignageQuoteInfo')}</Typography>
                    {width === 'xs' ? (
                      exteriorSignageQuotes?.items?.map(exteriorSignageQuote => (
                        <CmtCard key={exteriorSignageQuote.id}>
                          <CmtCardHeader
                            separator={{
                              color: theme.palette.borderColor.dark,
                            }}
                            title={
                              exteriorSignageQuote.quoteStatusHistories?.find(
                                history => history.newStatus === ExteriorSignageQuoteStatusEnumValue.Submitted,
                              )?.dateStatusChanged
                                ? moment(
                                    exteriorSignageQuote.quoteStatusHistories?.find(
                                      history => history.newStatus === ExteriorSignageQuoteStatusEnumValue.Submitted,
                                    )?.dateStatusChanged,
                                  ).format(t('date_format'))
                                : ''
                            }
                            subTitle={translateEnum(ExteriorSignageQuoteStatusEnum, exteriorSignageQuote.status)}
                          />
                          <CmtCardContent>
                            <CmtGridView
                              itemPadding={24}
                              responsive={{
                                xs: 1,
                                sm: 2,
                                md: 3,
                                lg: 3,
                                xl: 3,
                              }}
                              data={[
                                {
                                  title: t('total'),
                                  desc: <Currency quantity={exteriorSignageQuote.total} currency={dealer?.currenceName} />,
                                  icon: <Money />,
                                },
                              ]}
                              renderRow={(item, index) => (
                                <Box display="flex" alignItems="center" key={index}>
                                  {item.icon}
                                  <Box ml={3}>
                                    <Box fontSize={12} color="text.secondary">
                                      {item.title}
                                    </Box>
                                    <Box>{item.desc ? item.desc : item.list}</Box>
                                  </Box>
                                </Box>
                              )}
                            />
                          </CmtCardContent>
                          <CmtCardFooter>
                            <Grid item xs={12} textAlign="center">
                              <Link
                                to={`/dealer/profile/awaiting-requests/exterior-signage-quote/${exteriorSignageQuote.id}`}>
                                <Button variant="outlined">{t('see_exterior_signage_quote')}</Button>
                              </Link>
                            </Grid>
                          </CmtCardFooter>
                        </CmtCard>
                      ))
                    ) : (
                      <Table>
                        <TableHead>
                          <TableRow>
                            {showDealerColumn && <TableCell>{t('dealer')}</TableCell>}
                            <TableCell>{t('quoteNumber')}</TableCell>
                            <TableCell>{t('date')}</TableCell>
                            <TableCell>{t('status')}</TableCell>
                            <TableCell>{t('total')}</TableCell>
                            <TableCell>{t('actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {exteriorSignageQuotes?.items?.map(exteriorSignageQuote => {
                            return (
                              <TableRow
                                key={exteriorSignageQuote.id}
                                style={
                                  exteriorSignageQuote.status === ExteriorSignageQuoteStatusEnumValue.PendingDealerApproval
                                    ? {
                                        backgroundColor: '#f6d5d5',
                                      }
                                    : {}
                                }>
                                {showDealerColumn && <TableCell>{exteriorSignageQuote.dealerName}</TableCell>}
                                <TableCell>{exteriorSignageQuote.quoteNumber}</TableCell>
                                <TableCell>
                                  {exteriorSignageQuote.quoteStatusHistories?.find(
                                    history => history.newStatus === ExteriorSignageQuoteStatusEnumValue.Submitted,
                                  )?.dateStatusChanged
                                    ? moment(
                                        exteriorSignageQuote.quoteStatusHistories?.find(
                                          history => history.newStatus === ExteriorSignageQuoteStatusEnumValue.Submitted,
                                        )?.dateStatusChanged,
                                      ).format(t('date_format'))
                                    : ''}
                                </TableCell>
                                <TableCell>
                                  {translateEnum(ExteriorSignageQuoteStatusEnum, exteriorSignageQuote.status)}
                                </TableCell>
                                <TableCell>
                                  <Currency quantity={exteriorSignageQuote.total} currency={dealer?.currenceName} />
                                </TableCell>
                                <TableCell>
                                  <ThemedTooltip title={t('see_exterior_signage_quote')} placement={'top'}>
                                    <Link
                                      to={`/dealer/profile/awaiting-requests/exterior-signage-quote/${exteriorSignageQuote.id}`}>
                                      <Fab
                                        color="primary"
                                        sx={{
                                          height: 26,
                                          width: 26,
                                          minHeight: 26,
                                          '& svg': {
                                            height: 14,
                                            width: 14,
                                          },
                                        }}>
                                        <Visibility />
                                      </Fab>
                                    </Link>
                                  </ThemedTooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}

                    <GridContainer spacing={0} className="mt-5"></GridContainer>
                  </CmtCardContent>
                </CmtCard>
              )}

              {currentTab === 2 && (
                <CmtCard>
                  <CmtCardHeader
                    style={{ minHeight: '60px' }}
                    separator={{
                      color: theme.palette.borderColor.dark,
                    }}
                    title={t('submittedMerchandisingProgramQuotes')}
                  />
                  <CmtCardContent>
                    <Typography variant="h4">{t('submittedMerchandisingProgramQuoteInfo')}</Typography>
                    {width === 'xs' ? (
                      merchandisingProgramQuotes?.items?.map(merchandisingProgramQuote => (
                        <CmtCard key={merchandisingProgramQuote.id}>
                          <CmtCardHeader
                            separator={{
                              color: theme.palette.borderColor.dark,
                            }}
                            title={
                              merchandisingProgramQuote.quoteStatusHistories?.find(
                                history => history.newStatus === MerchandisingProgramQuoteStatusEnumValue.InProcess,
                              )?.dateStatusChanged
                                ? moment(
                                    merchandisingProgramQuote.quoteStatusHistories?.find(
                                      history => history.newStatus === MerchandisingProgramQuoteStatusEnumValue.InProcess,
                                    )?.dateStatusChanged,
                                  ).format(t('date_format'))
                                : ''
                            }
                            subTitle={translateEnum(MerchandisingProgramQuoteStatusEnum, merchandisingProgramQuote.status)}
                          />
                          <CmtCardContent>
                            <CmtGridView
                              itemPadding={24}
                              responsive={{
                                xs: 1,
                                sm: 2,
                                md: 3,
                                lg: 3,
                                xl: 3,
                              }}
                              data={[
                                {
                                  title: t('total'),
                                  desc: (
                                    <Currency quantity={merchandisingProgramQuote.total} currency={dealer?.currenceName} />
                                  ),
                                  icon: <Money />,
                                },
                              ]}
                              renderRow={(item, index) => (
                                <Box display="flex" alignItems="center" key={index}>
                                  {item.icon}
                                  <Box ml={3}>
                                    <Box fontSize={12} color="text.secondary">
                                      {item.title}
                                    </Box>
                                    <Box>{item.desc ? item.desc : item.list}</Box>
                                  </Box>
                                </Box>
                              )}
                            />
                          </CmtCardContent>
                          <CmtCardFooter>
                            <Grid item xs={12} textAlign="center">
                              <Link
                                to={`/dealer/profile/awaiting-requests/merchandising-program-quote/${merchandisingProgramQuote.id}`}>
                                <Button variant="outlined">{t('see_merchandising_program_quote')}</Button>
                              </Link>
                            </Grid>
                          </CmtCardFooter>
                        </CmtCard>
                      ))
                    ) : (
                      <Table>
                        <TableHead>
                          <TableRow>
                            {showDealerColumn && <TableCell>{t('dealer')}</TableCell>}
                            <TableCell>{t('quoteNumber')}</TableCell>
                            <TableCell>{t('date')}</TableCell>
                            <TableCell>{t('status')}</TableCell>
                            <TableCell>{t('total')}</TableCell>
                            <TableCell>{t('actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {merchandisingProgramQuotes?.items?.map(merchandisingProgramQuote => {
                            return (
                              <TableRow key={merchandisingProgramQuote.id}>
                                {showDealerColumn && <TableCell>{merchandisingProgramQuote.dealerName}</TableCell>}
                                <TableCell>{merchandisingProgramQuote.quoteNumber}</TableCell>

                                <TableCell>
                                  {merchandisingProgramQuote.quoteStatusHistories?.find(
                                    history => history.newStatus === MerchandisingProgramQuoteStatusEnumValue.InProcess,
                                  )?.dateStatusChanged
                                    ? moment(
                                        merchandisingProgramQuote.quoteStatusHistories?.find(
                                          history =>
                                            history.newStatus === MerchandisingProgramQuoteStatusEnumValue.InProcess,
                                        )?.dateStatusChanged,
                                      ).format(t('date_format'))
                                    : ''}
                                </TableCell>
                                <TableCell>
                                  {translateEnum(MerchandisingProgramQuoteStatusEnum, merchandisingProgramQuote.status)}
                                </TableCell>
                                <TableCell>
                                  <Currency quantity={merchandisingProgramQuote.total} currency={dealer?.currenceName} />
                                </TableCell>
                                <TableCell>
                                  <ThemedTooltip title={t('see_merchandising_program_quote')} placement={'top'}>
                                    <Link
                                      to={`/dealer/profile/awaiting-requests/merchandising-program-quote/${merchandisingProgramQuote.id}`}>
                                      <Fab
                                        color="primary"
                                        sx={{
                                          height: 26,
                                          width: 26,
                                          minHeight: 26,
                                          '& svg': {
                                            height: 14,
                                            width: 14,
                                          },
                                        }}>
                                        <Visibility />
                                      </Fab>
                                    </Link>
                                  </ThemedTooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}

                    <GridContainer spacing={0} className="mt-5"></GridContainer>
                  </CmtCardContent>
                </CmtCard>
              )}
            </GridItem>
          </GridContainer>
        </CmtCardContent>
      </CmtCard>
    </Box>
  );
};

export default ProfileAwaitingRequests;
